import { UntypedFormGroup } from "@angular/forms";
import { Tubular } from "../models";
import { WellComponent } from "../models/well-component";

interface IPipeSection {
    top: number,
    btm: number,
    diameter: number
}

export function getWellboreOutsideString(tubularString: WellComponent, wellConfig: WellComponent[]): {priorShoeMd: number, wellboreSections: IPipeSection[]} {

    const wellbore = { priorShoeMd: 0, wellboreSections: [] };
    const wellConfigIdx = wellConfig.findIndex(wc => wc.id == tubularString.id);
    const previousString = wellConfig[wellConfigIdx - 1];
  
    if (!previousString) {
        wellbore.wellboreSections.push({top:0, btm: tubularString.shoeMd, diameter: tubularString.holeSize });
        return wellbore;
    }

    // Looks like this takes the open hole holesize at the bottom section of the wellbore?
    let bottomOfWellboreSection = tubularString.shoeMd;
    if (previousString.shoeMd < bottomOfWellboreSection && tubularString.holeSize > 0) {
        wellbore.wellboreSections.push({ top: previousString.shoeMd, btm: bottomOfWellboreSection, diameter: tubularString.holeSize });
        bottomOfWellboreSection = previousString.shoeMd;
        wellbore.priorShoeMd = bottomOfWellboreSection; //TODO: May not need this client side...
    }

    const wellConfigBeforeCurrentString = wellConfig.slice(0, wellConfigIdx);

    //Add in topMd to string sections (doing this on the client because we don't have the top depth)
    for (const ts of wellConfigBeforeCurrentString) {
        let topMd = ts.hangerMd;
        for(const ss of ts.stringSections){
            ss.topMeasuredDepth = topMd;
            topMd = ss.bottomMeasuredDepth;
        }
    }

    for (const ts of wellConfigBeforeCurrentString.reverse()) {
        if (ts.hangerMd >= tubularString.shoeMd) {
            continue;
        }
        for (const ss of ts.stringSections.reverse()) {
            const topOfWellboreSection = ss.topMeasuredDepth;
            if (Math.abs(topOfWellboreSection - bottomOfWellboreSection) < 0.0001 || topOfWellboreSection > bottomOfWellboreSection) {
                continue;
            }
            wellbore.wellboreSections.splice(0, 0, { top: topOfWellboreSection, btm: bottomOfWellboreSection, diameter: ss.pipe.insideDiameter });
            bottomOfWellboreSection = topOfWellboreSection;
        }
        if (Math.abs(ts.hangerMd - tubularString.hangerMd) < 0.0001) {
            break;
        }
    }

    return wellbore;

}

export function getWellboreMinimumDiameter(topMd: number, bottomMd: number, wellbore: IPipeSection[]): number {
    const wellboreDiameters = wellbore.filter(x => x.btm >= topMd && x.top <= bottomMd).map(ws => ws.diameter - 0.1).filter(x => !!x); // Last filter pulls undefined, "", null, 0
    return Math.min(...wellboreDiameters);
}

/**
 * Returns boolean indicating if pipe will get stuck in the wellbore.
 * If no constriction is not found, return false;
 * If a constriction is found, returns true.
 * @param {WellComponent} - Well components
 * @param {Array<IWellbore>}} - Collection of wellbore sections
 */
export function checkFitInWellbore(tubular: WellComponent, wellbore: IPipeSection[]): boolean {
    const stringSections: IPipeSection[] = tubular.stringSections.map((ss, i) => {
        return {
            top: (i == 0) ? tubular.hangerMd : tubular.stringSections[i - 1].bottomMeasuredDepth,
            btm: ss.bottomMeasuredDepth,
            diameter: ss.pipe.outsideDiameter
        };
    });
    return stringSections.every(ss => ss.diameter <= getWellboreMinimumDiameter(ss.top, ss.btm, wellbore));
}

export function getMinimumDiameterFittingWellbore(tubular: WellComponent, stringSection: Tubular, wellbore: IPipeSection[]): number {
    const stringSections: IPipeSection[] = tubular.stringSections.map((ss, i) => {
        return {
            top: (i == 0) ? tubular.hangerMd : tubular.stringSections[i - 1].bottomMeasuredDepth,
            btm: ss.bottomMeasuredDepth,
            diameter: ss.pipe.insideDiameter
        };
    });
    const stringSectionWithTop = stringSections.find(ss => ss.btm == stringSection.bottomMeasuredDepth && ss.diameter == stringSection.pipe.insideDiameter);
    return  getWellboreMinimumDiameter(stringSectionWithTop.top, stringSectionWithTop.btm, wellbore);
}

export function stringSectionsMustFitInWellbore(control: UntypedFormGroup) {
    const wellConfiguration = control?.parent?.getRawValue() as WellComponent[]; //Using RawValue so we can get the disabled inputs.
    const tubularString = control?.getRawValue() as WellComponent;
    if (tubularString && wellConfiguration && !control.pristine) { // Checking pristine avoids doing this when the form is loading
        const wellboreProfile = getWellboreOutsideString(tubularString, wellConfiguration);
        const fitsInWellbore = checkFitInWellbore(tubularString, wellboreProfile.wellboreSections);
        if (!fitsInWellbore) {
            return { error: "A string section in this tubular fails drift clearance in the wellbore. Check your design." }
        }
    }
    return null;
}
